import Cookies from 'js-cookie'
import { getPageContext, getPageRouteName } from './page-context'
import { v4 as uuidv4 } from 'uuid'
import type { SegmentTypes } from '~/types/api/rental-segment-types'
import { PropertyMetadata as SegmentEventMetadata } from '~/types/api/rental-segment-types'
import type { SegmentIdentify, SegmentOptions, SignupSourceType } from '~/types/segment'
import { COOKIES as ConstantCookies } from '#shared/constants/cookies'
import mobileCapabilities, { FEATURES } from '#shared/lib/mobileCapabilities'
import type { UseAuthentication } from '~/types/rental-api-aliases'
import type { AndroidData, IosData } from '~/types/mobile-bridge'
import type { SegmentPageContext } from './page-context'

/**
 * TODO: this file needs work, as it depends on some composables.
 * We probably should just pass params down to the pure functions.
 */

type SegmentValidator = (eventName: keyof SegmentTypes, payload: unknown) => void

let segmentValidator: SegmentValidator | undefined

/**
 * Registers a validator for serialized payloads. Only registered in dev and
 * tests, so production never loads Ajv.
 *
 * @param validator The validator to use.
 */
export function setSegmentValidator(validator: SegmentValidator | undefined) {
  segmentValidator = validator
}

export function trackSegmentEvent<T extends keyof SegmentTypes>(
  eventName: T,
  data: SegmentTypes[T],
  options?: SegmentOptions,
) {
  log('trackSegmentEvent', { eventName, data, options })

  // Validate payloads in the actual format that Segment consumes, so we catch
  // NaN/null/undefined issues at the source.
  segmentValidator?.(eventName, JSON.parse(JSON.stringify(data)))

  const $config = useRuntimeConfig().public.segment
  const metadata = SegmentEventMetadata[eventName]
  const { isWebView } = usePlatform()

  // When is mobile webview, delegates to firebase
  if (isWebView.value) {
    const enabledEventVersioning = mobileCapabilities(Cookies.get(ConstantCookies.capabilities), [FEATURES.CAN_TRACK_SEGMENT_VERSION])
    const androidData: AndroidData = {
      func: 'trackSegmentEvent',
      params: [
        metadata.name,
        JSON.stringify(data),
      ],
    }
    const iosData: IosData = {
      command: 'trackSegmentEvent',
      name: metadata.name,
      parameters: JSON.parse(JSON.stringify(data)),
    }

    if (enabledEventVersioning && metadata.version) {
      (androidData.params as [string, string, number]).push(metadata.version)
      iosData.eventVersion = metadata.version
    }

    postMessageToMobileApp({
      android: androidData,
      ios: iosData,
    })

    return
  }

  if (!options) options = {}
  if (!options.context) options.context = {}
  if (!options.context.protocols) options.context.protocols = {}
  options.context.protocols.event_version = metadata.version

  if ($config.enabled) {
    const page = getPageContext()
    if (page) {
      // Values passed in by the caller win over the page snapshot
      options.context.page = { ...page, ...options.context.page }
    }

    window.analytics?.track(metadata.name, data, options)
  }

  // Fire clarity together with segment events
  SendEventToClarity(metadata.name)
}

export function segmentIdentify(user: UseAuthentication) {
  log('segmentIdentify', { user })

  let phone = ''
  if (user.PhoneNumber) {
    phone = user.PhoneNumber
    if (!user.PhoneNumber.includes('+') && user.CountryPhoneCode) {
      phone = `${user.CountryPhoneCode}${user.PhoneNumber}`
    }
  }

  const signupSourceArr = ['IOS', 'Android']
  let userSignupSource: SignupSourceType = 'Web'
  if (user.SignupFrom && signupSourceArr.includes(user.SignupFrom)) {
    userSignupSource = user.SignupFrom as SignupSourceType
  }

  const identifyData: SegmentIdentify = {
    email: user.Email as string,
    firstName: user.FirstName as string,
    lastName: user.LastName as string,
    phone: phone,
    language: user.Language === 'French' ? 'fr' : 'en',
    createdAt: user.CreatedDate,
    lastLogin: user.LastLogin,
    isOwner: user.IsOwner,
    hasRVListed: user.HasRv,
    signupSource: userSignupSource,
    isSuperhost: user?.SuperHost?.IsActive,
  }

  window?.analytics?.identify(user.Id.toString(), identifyData)
}

export const segmentReset = () => {
  log('segmentReset')

  window?.analytics?.reset()
}

export const segmentPage = (page: SegmentPageContext, $config: ReturnType<typeof useRuntimeConfig>['public']) => {
  log('segmentPage', page)

  if (!$config.segment.enabled) return

  const routeName = getPageRouteName()

  window.analytics?.page(
    routeName ?? undefined,
    { ...page },
    { context: { page } },
  )
}

export const getSegmentAnonymousId = () => {
  log('getSegmentAnonymousId')

  // https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/identity/
  // Return cookie or localStorage
  let anonymousId
    = Cookies.get(ConstantCookies.ajsAnonymousId) ?? JSON.parse(window?.localStorage?.getItem(ConstantCookies.ajsAnonymousId) || null!)

  if (!anonymousId) {
    // Create a new UUID if no anonymousId are retrived
    anonymousId = uuidv4()
    window?.analytics?.setAnonymousId(anonymousId)
  }

  return anonymousId
}
